import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, AlertTriangle, ChevronRight, Eye, Upload,
  CheckCircle2, XCircle, RefreshCw,
} from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";

const STAGING_TAB = "Products_Staging";
const CONFIRM_PHRASE = "IMPORT";

function Stat({ label, value, tone }) {
  const toneClass =
    tone === "good"   ? "text-emerald-600" :
    tone === "warn"   ? "text-amber-600" :
    tone === "bad"    ? "text-rose-600"   :
    tone === "muted"  ? "text-ink/50"     : "text-ink";
  return (
    <div className="flex flex-col gap-0.5 min-w-[88px]">
      <div className="data-sku text-[10px]">{label}</div>
      <div className={`font-display font-black text-2xl leading-none ${toneClass}`}>{value}</div>
    </div>
  );
}

function StepHeader({ index, title, status }) {
  const tone =
    status === "done"    ? "bg-emerald-600 text-white"
  : status === "active"  ? "bg-ink text-white"
  : "bg-secondary text-ink/50";
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center font-display font-black text-sm ${tone}`}>
        {status === "done" ? <CheckCircle2 size={16} /> : index}
      </div>
      <h2 className="font-display font-black text-lg sm:text-xl tracking-tight">{title}</h2>
    </div>
  );
}

export default function CatalogImportPage() {
  const [depts, setDepts] = useState([]);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [limit, setLimit] = useState("");

  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const [confirmText, setConfirmText] = useState("");

  // Load departments — pre-select Cheese (current pilot)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/departments");
        setDepts(data);
        const cheese = data.find((d) => (d.name || "").toLowerCase() === "cheese");
        if (cheese) setSelectedDepts([cheese.name]);
        else if (data[0]) setSelectedDepts([data[0].name]);
      } catch (e) {
        toast.error(formatApiErrorDetail(e));
      }
    })();
  }, []);

  const limitInt = useMemo(() => {
    if (limit === "" || limit === null) return null;
    const n = parseInt(limit, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [limit]);

  const limitInvalid = limit !== "" && limitInt === null;

  const validateStep = async () => {
    setValidating(true);
    setValidation(null);
    setPreview(null);
    setResult(null);
    try {
      const { data } = await api.get("/admin/catalog/validate", {
        params: { tab: STAGING_TAB },
      });
      setValidation(data);
      if (data.ok) toast.success("Validation passed");
      else toast.warning(`Validation found ${data.error_count} error${data.error_count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setValidating(false);
    }
  };

  const runDryRun = async () => {
    if (selectedDepts.length === 0) {
      toast.error("Pick at least one department");
      return;
    }
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const { data } = await api.post("/admin/catalog/import-staging", {
        departments: selectedDepts,
        dry_run: true,
        ...(limitInt !== null ? { limit: limitInt } : {}),
      });
      setPreview(data);
      toast.success(`Preview ready — would import ${data.would_import_count} row${data.would_import_count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setPreviewing(false);
    }
  };

  const commitImport = async () => {
    if (confirmText.trim() !== CONFIRM_PHRASE) {
      toast.error(`Type ${CONFIRM_PHRASE} to confirm`);
      return;
    }
    setCommitting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/catalog/import-staging", {
        departments: selectedDepts,
        dry_run: false,
        ...(limitInt !== null ? { limit: limitInt } : {}),
      });
      setResult(data);
      setConfirmText("");
      toast.success(`Imported ${data.imported_count} product${data.imported_count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setCommitting(false);
    }
  };

  const toggleDept = (name) => {
    setSelectedDepts((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
    setPreview(null);
    setResult(null);
  };

  const step1Status =
    validating ? "active" : validation ? (validation.ok ? "done" : "active") : "active";
  const step2Status =
    !validation || !validation.ok ? "idle"
  : previewing ? "active"
  : preview ? "done" : "active";
  const step3Status =
    !preview ? "idle"
  : committing ? "active"
  : result ? "done" : "active";

  const previewCounts = preview ? [
    { label: "Total rows",        value: preview.total_staging_rows,            tone: "muted" },
    { label: "Eligible",          value: preview.eligible_rows,                 tone: "good"  },
    { label: "Would import",      value: preview.would_import_count,            tone: "good"  },
    { label: "Errors",            value: preview.skipped_error_count,           tone: "bad"   },
    { label: "Inactive",          value: preview.skipped_inactive_count,        tone: "muted" },
    { label: "Other depts",       value: preview.skipped_department_filter_count, tone: "muted" },
    { label: "Already in Sheet",  value: preview.skipped_existing_live_count,   tone: "warn"  },
    { label: "Already in Mongo",  value: preview.skipped_existing_mongo_count,  tone: "warn"  },
  ] : [];

  return (
    <div className="max-w-4xl" data-testid="admin-catalog-import-page">
      <PageHeader
        eyebrow="Catalog management"
        title="Catalog Import"
        subtitle={
          <>
            Pulls validated rows from the{" "}
            <span className="font-mono">Products_Staging</span> Google Sheet tab
            into the live MongoDB catalog. Idempotent — barcodes that already
            exist are always skipped.
          </>
        }
      />

      {/* STEP 1 — VALIDATE */}
      <Card variant="raised" className="p-5 mb-4" data-testid="step-validate">
        <StepHeader index={1} title="Validate staging" status={step1Status} />
        <p className="text-sm text-ink/60 mb-4">
          Reads <span className="font-mono">{STAGING_TAB}</span> and checks every row. Nothing is written.
        </p>
        <Button
          tier="primary"
          onClick={validateStep}
          disabled={validating}
          data-testid="validate-button"
        >
          {validating ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
          {validating ? "Validating…" : validation ? "Re-validate" : "Run validation"}
        </Button>

        {validation && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="validation-summary">
            <Stat label="Total rows"      value={validation.total_rows} tone="muted" />
            <Stat label="Valid"           value={validation.valid_rows} tone="good" />
            <Stat label="Errors"          value={validation.error_count} tone={validation.error_count ? "bad" : "muted"} />
            <Stat label="Warnings"        value={validation.warning_count} tone={validation.warning_count ? "warn" : "muted"} />
            <Stat label="Already in Mongo" value={validation.existing_in_mongo_count} tone={validation.existing_in_mongo_count ? "warn" : "muted"} />
          </div>
        )}

        {validation?.errors?.length > 0 && (
          <div className="mt-4 border border-rose-200 bg-rose-50/60 rounded-sm p-3" data-testid="validation-errors">
            <div className="font-display font-bold text-sm text-rose-700 mb-2 flex items-center gap-1.5">
              <XCircle size={14} /> {validation.error_count} blocking error{validation.error_count === 1 ? "" : "s"}
            </div>
            <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
              {validation.errors.slice(0, 25).map((er, i) => (
                <li key={i} className="font-mono text-ink/80">
                  Row {er.row ?? "?"} — {er.message || er.reason || JSON.stringify(er)}
                </li>
              ))}
              {validation.errors.length > 25 && (
                <li className="text-ink/50 italic">…{validation.errors.length - 25} more</li>
              )}
            </ul>
          </div>
        )}

        {validation && validation.error_count === 0 && validation.warning_count > 0 && (
          <div className="mt-3 text-xs text-amber-700 flex items-center gap-1.5" data-testid="validation-warnings-hint">
            <AlertTriangle size={12} /> {validation.warning_count} warning{validation.warning_count === 1 ? "" : "s"} — safe to proceed; they will be skipped/handled at import.
          </div>
        )}
      </Card>

      {/* STEP 2 — DRY-RUN PREVIEW */}
      <Card
        variant="raised"
        className={`p-5 mb-4 transition-opacity ${(!validation || !validation.ok) ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="step-preview"
      >
        <StepHeader index={2} title="Preview import" status={step2Status} />
        <p className="text-sm text-ink/60 mb-4">
          Dry-run against the selected department(s). Writes nothing.
        </p>

        <div className="mb-4">
          <div className="data-sku mb-2">Departments</div>
          <div className="flex flex-wrap gap-2" data-testid="department-chips">
            {depts.length === 0 ? (
              <div className="text-xs text-ink/50">No departments configured</div>
            ) : depts.map((d) => {
              const active = selectedDepts.includes(d.name);
              return (
                <button
                  key={d.id}
                  onClick={() => toggleDept(d.name)}
                  className={`h-9 px-3 rounded-chip text-sm font-display tracking-wide transition-all duration-150 ease-out active:scale-[0.98] ${
                    active
                      ? "bg-ink text-white shadow-rest"
                      : "bg-paper-2 text-ink/70 shadow-rest hover:shadow-raised"
                  }`}
                  data-testid={`dept-chip-${d.name.toLowerCase()}`}
                >
                  {deptLabel(d.name)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4 max-w-xs">
          <div className="data-sku mb-1">Limit (optional)</div>
          <input
            type="number"
            min="0"
            value={limit}
            onChange={(e) => { setLimit(e.target.value); setPreview(null); setResult(null); }}
            placeholder="e.g. 50"
            className="w-full h-11 px-3 border border-border/60 rounded-button bg-paper-2 shadow-rest focus:shadow-raised focus:outline-none transition-shadow"
            data-testid="limit-input"
          />
          {limitInvalid && (
            <div className="text-xs text-rose-600 mt-1">Limit must be a non-negative integer</div>
          )}
        </div>

        <Button
          tier="secondary"
          onClick={runDryRun}
          disabled={previewing || selectedDepts.length === 0 || limitInvalid}
          data-testid="preview-button"
        >
          {previewing ? <Loader2 className="animate-spin" size={16} /> : <Eye size={16} />}
          {previewing ? "Previewing…" : preview ? "Re-run preview" : "Preview (dry-run)"}
        </Button>

        {preview && (
          <>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="preview-summary">
              {previewCounts.map((c) => <Stat key={c.label} {...c} />)}
            </div>

            {preview.limited_by === "limit" && (
              <div className="mt-3 text-xs text-amber-700 flex items-center gap-1.5">
                <AlertTriangle size={12} />
                Capped by limit. {preview.eligible_rows - preview.would_import_count} more eligible rows are waiting beyond the cap.
              </div>
            )}

            {preview.sample_imports?.length > 0 && (
              <div className="mt-4">
                <div className="data-sku mb-2">Sample of rows to import</div>
                <div className="border border-border rounded-sm overflow-hidden">
                  {preview.sample_imports.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-3 py-2 text-sm border-b border-border last:border-0"
                      data-testid={`preview-sample-${i}`}
                    >
                      <div className="font-mono text-xs text-ink/50 w-12">#{s.row}</div>
                      <div className="font-mono text-xs text-ink/70 w-28 truncate">{s.barcode}</div>
                      <div className="flex-1 font-display font-semibold truncate">{s.name}</div>
                      <div className="data-sku text-[10px]">{s.department}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preview.sample_skips?.length > 0 && (
              <div className="mt-4">
                <div className="data-sku mb-2 text-rose-700">Sample skipped (errors / inactive)</div>
                <div className="border border-rose-200 bg-rose-50/40 rounded-sm overflow-hidden">
                  {preview.sample_skips.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-3 py-2 text-xs border-b border-rose-100 last:border-0"
                    >
                      <div className="font-mono text-ink/50 w-12">#{s.row}</div>
                      <div className="font-mono text-ink/70 w-28 truncate">{s.barcode || "—"}</div>
                      <div className="flex-1 text-ink/80">{s.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* STEP 3 — COMMIT */}
      <Card
        variant="raised"
        className={`p-5 mb-10 transition-opacity ${!preview ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="step-commit"
      >
        <StepHeader index={3} title="Confirm & import" status={step3Status} />

        {preview && (
          <p className="text-sm text-ink/70 mb-4">
            Will insert <span className="font-display font-bold text-ink">{preview.would_import_count}</span> product
            {preview.would_import_count === 1 ? "" : "s"} into MongoDB
            (department{selectedDepts.length === 1 ? "" : "s"}:&nbsp;
            <span className="font-display font-semibold text-ink">{selectedDepts.join(", ")}</span>).
            This action is logged to Action History.
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-end max-w-xl">
          <div className="flex-1">
            <div className="data-sku mb-1">Type <span className="font-mono text-ink">{CONFIRM_PHRASE}</span> to confirm</div>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              className="w-full h-11 px-3 border border-border/60 rounded-button bg-paper-2 shadow-rest focus:shadow-raised focus:outline-none transition-shadow font-mono tracking-widest uppercase"
              data-testid="confirm-input"
              disabled={!preview || committing}
            />
          </div>
          <Button
            tier="primary"
            onClick={commitImport}
            disabled={!preview || committing || confirmText.trim() !== CONFIRM_PHRASE || (preview && preview.would_import_count === 0)}
            className="sm:w-auto w-full"
            data-testid="commit-button"
          >
            {committing ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            {committing ? "Importing…" : "Import now"}
          </Button>
        </div>

        {result && (
          <div className="mt-5" data-testid="import-result">
            <div className="flex items-center gap-2 mb-3 text-emerald-700 font-display font-bold">
              <CheckCircle2 size={18} /> Import complete
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Imported"          value={result.imported_count} tone="good" />
              <Stat label="Eligible"          value={result.eligible_rows} tone="muted" />
              <Stat label="Errors"            value={result.skipped_error_count} tone={result.skipped_error_count ? "bad" : "muted"} />
              <Stat label="Inactive"          value={result.skipped_inactive_count} tone="muted" />
              <Stat label="Other depts"       value={result.skipped_department_filter_count} tone="muted" />
              <Stat label="In live Sheet"     value={result.skipped_existing_live_count} tone="warn" />
              <Stat label="In Mongo"          value={result.skipped_existing_mongo_count} tone="warn" />
              <Stat label="Limited by"        value={result.limited_by || "—"} tone="muted" />
            </div>

            <div className="mt-4 flex gap-3">
              <Button
                tier="secondary"
                onClick={() => {
                  setValidation(null);
                  setPreview(null);
                  setResult(null);
                  setConfirmText("");
                  validateStep();
                }}
                data-testid="restart-button"
              >
                <RefreshCw size={14} /> Start over
              </Button>
              <Button
                tier="secondary"
                onClick={() => { window.location.href = "/admin/products"; }}
                data-testid="goto-products-button"
              >
                View products <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
